import { useTranslationHelper } from '@shared/hooks/useTranslationsHelper';
import { MdcPagerChangeEvent, MdcPagerCustomEvent } from 'ids-wc';
import { MdcBox, MdcPager, MdcSelectField, MdcTypography } from 'ids-wc/dist/react/components';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import style from './PagerWithSize.module.css';

interface PagerWithSizeProps {
  onChange: (pageSize: number, page: number) => void;
  total?: number;
  pageSizeOptions?: number[];
}

export type PagerWithSizeRef = {
  reset: () => void;
};

export const PAGER_PAGE_SIZES = [5, 10, 20, 50];

export const PAGER_FIRST_PAGE = 1;

export const PagerWithSize = forwardRef<PagerWithSizeRef, PagerWithSizeProps>((props, ref) => {
  const { total, onChange, pageSizeOptions } = props;
  const [pageSize, setPageSize] = useState(pageSizeOptions ? pageSizeOptions[0] : PAGER_PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState(PAGER_FIRST_PAGE);
  const [totalPages, setTotalPages] = useState(0);
  const { t } = useTranslationHelper('pagerWithSize');

  const availablePageSizes = pageSizeOptions || PAGER_PAGE_SIZES;

  useEffect(() => {
    if (!total) return;
    setTotalPages(Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const onPageSizeChange = (e: CustomEvent) => {
    setPageSize(e.detail.values[0].value);
    setCurrentPage(PAGER_FIRST_PAGE);
    onChange(e.detail.values[0].value, PAGER_FIRST_PAGE);
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
    onChange(pageSize, page);
  };

  const reset = () => {
    setPageSize(availablePageSizes[0]);
    setCurrentPage(PAGER_FIRST_PAGE);
  };

  useImperativeHandle(ref, () => ({ reset }));

  if (!total || total <= availablePageSizes[0]) return null;

  const pageSizeValue = pageSize.toString();

  return (
    <MdcBox display="flex" justifyContent="between" alignItems="center">
      <MdcPager
        pages={totalPages}
        current={currentPage}
        color="primary"
        labelFirstPage={t('first')}
        labelLastPage={t('last')}
        labelPrevPage={t('previous')}
        labelNextPage={t('next')}
        onMdcPagerChange={(e: MdcPagerCustomEvent<MdcPagerChangeEvent>) => onPageChange(e.detail.index)}
      />

      <MdcBox display="flex" alignItems="center" gap={1}>
        <MdcBox displayLg="block" display="none">
          <MdcTypography variant="body2" text={t('resultsPerPage')} />
        </MdcBox>
        <MdcSelectField
          id="pageSizeSelect"
          name="page-size"
          values={[{ text: pageSizeValue, value: pageSizeValue }]}
          disableClear
          disableFullWidth
          defaultValue={pageSizeValue}
          list={availablePageSizes.map((size) => ({ text: size.toString(), value: size.toString() }))}
          onMdcSelectChange={onPageSizeChange}
          className={style['size-selector']}
          variant="outlined"
        />
      </MdcBox>
    </MdcBox>
  );
});

PagerWithSize.displayName = 'PagerWithSize';
